import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Phone, Loader2, CheckCircle, MapPin } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const DriverLeadForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [location, setLocation] = useState(null);

  // Get user's location on mount
  React.useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (err) => {
          console.log('Location not available:', err.message);
        }
      );
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.name.trim()) {
      setError('Please enter your name / உங்கள் பெயரை உள்ளிடவும்');
      return;
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      setError('Please enter a valid phone number / சரியான தொலைபேசி எண்ணை உள்ளிடவும்');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/public/driver-lead-form`, {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        location: location
      });

      if (response.data.success) {
        navigate('/driver-lead-form/thank-you');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again. / ஏதோ தவறு ஏற்பட்டது. மீண்டும் முயற்சிக்கவும்.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Nura Logo */}
        <div className="text-center mb-8">
          <img 
            src="/nura-black.png" 
            alt="Nura" 
            className="h-16 mx-auto mb-6"
          />
          <h1 className="text-2xl font-bold text-gray-900 mb-1">
            Join Our Driver Network
          </h1>
          <p className="text-lg text-gray-600">
            எங்கள் டிரைவர் நெட்வொர்க்கில் சேரவும்
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name / முழு பெயர்
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter your full name / உங்கள் முழு பெயரை உள்ளிடவும்"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none"
                disabled={loading}
                data-testid="name-input"
              />
            </div>
          </div>

          {/* Phone Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number / தொலைபேசி எண்
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                placeholder="Enter your phone number / உங்கள் தொலைபேசி எண்ணை உள்ளிடவும்"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-base focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all outline-none"
                disabled={loading}
                maxLength={10}
                data-testid="phone-input"
              />
            </div>
          </div>

          {/* Location indicator */}
          {location && (
            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
              <MapPin className="w-4 h-4" />
              <span>Location captured / இருப்பிடம் பதிவு செய்யப்பட்டது</span>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-base rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Submitting... / சமர்ப்பிக்கிறது...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Submit / சமர்ப்பிக்கவும்
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-6">
          By submitting, you agree to our Terms & Conditions
          <br />
          சமர்ப்பிப்பதன் மூலம், நீங்கள் எங்கள் விதிமுறைகளை ஏற்றுக்கொள்கிறீர்கள்
        </p>
      </div>
    </div>
  );
};

export default DriverLeadForm;
